export type PostTextSegment =
  | { kind: "text"; value: string }
  | { kind: "link"; value: string; url: string };

const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<>{}\[\]"']+/gi;
const SIMPLE_TRAILING_PUNCTUATION = new Set([".", ",", "!", "?", ";", ":"]);
const PAIRED_PUNCTUATION: Array<[string, string]> = [
  ["(", ")"],
  ["[", "]"],
  ["{", "}"],
];

function countCharacter(value: string, character: string) {
  let count = 0;
  for (const current of value) {
    if (current === character) count += 1;
  }
  return count;
}

function splitTrailingPunctuation(value: string) {
  let linkValue = value;
  let suffix = "";

  while (linkValue && SIMPLE_TRAILING_PUNCTUATION.has(linkValue.at(-1) ?? "")) {
    suffix = `${linkValue.at(-1)}${suffix}`;
    linkValue = linkValue.slice(0, -1);
  }

  for (const [open, close] of PAIRED_PUNCTUATION) {
    while (
      linkValue.endsWith(close) &&
      countCharacter(linkValue, close) > countCharacter(linkValue, open)
    ) {
      suffix = `${close}${suffix}`;
      linkValue = linkValue.slice(0, -1);
    }
  }

  return { linkValue, suffix };
}

export function normalizePostUrl(value: string): string | null {
  const trimmed = value.trim();
  const normalized = /^www\./i.test(trimmed) ? `https://${trimmed}` : trimmed;

  try {
    const parsed = new URL(normalized);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    if (!parsed.hostname) return null;
    return normalized;
  } catch {
    return null;
  }
}

export function linkifyPostText(text: string): PostTextSegment[] {
  if (!text) return [];

  const segments: PostTextSegment[] = [];
  const pattern = new RegExp(URL_PATTERN.source, URL_PATTERN.flags);
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text))) {
    const rawValue = match[0];
    const { linkValue, suffix } = splitTrailingPunctuation(rawValue);
    const normalizedUrl = normalizePostUrl(linkValue);

    if (!normalizedUrl) continue;

    if (match.index > cursor) {
      segments.push({ kind: "text", value: text.slice(cursor, match.index) });
    }

    segments.push({ kind: "link", value: linkValue, url: normalizedUrl });
    if (suffix) segments.push({ kind: "text", value: suffix });

    cursor = match.index + rawValue.length;
  }

  if (cursor < text.length) {
    segments.push({ kind: "text", value: text.slice(cursor) });
  }

  return segments.length > 0 ? segments : [{ kind: "text", value: text }];
}
